import React, { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { buildTimelineLayout } from "./timeline/timelineLayout";
import { useTimelineData } from "./timeline/useTimelineData";
import { TimelineCard } from "./timeline/TimelineCard";
import { TimelineControls } from "./timeline/TimelineControls";
import { UndatedTray } from "./timeline/UndatedTray";
import { EraAxis } from "./timeline/EraAxis";

export function TimelineView({ onSelect, activeId }) {
  const { data, loading } = useTimelineData();
  const [showUndated, setShowUndated] = useState(false);
  const [filterKs, setFilterKs] = useState("all");
  const isDimmed = (item) =>
    filterKs !== "all" && !(item.knowledge_systems || []).some((k) => k.id === filterKs);

  const layout = useMemo(() => buildTimelineLayout(data), [data]);

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[#14140f]/40" data-testid="timeline-loading">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (!data?.items?.length) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[#4e6073] text-sm">
        No dated texts or thinkers to place on the timeline yet.
      </div>
    );
  }

  const { texts, persons, totalW, ticks, textLaneH, personLaneH } = layout;

  return (
    <div className="absolute inset-0 flex flex-col bg-[#fdfdfb]" data-testid="timeline-view">
      <TimelineControls
        systems={data.knowledge_systems}
        filterKs={filterKs}
        setFilterKs={setFilterKs}
        undatedCount={data.undated?.length || 0}
        showUndated={showUndated}
        setShowUndated={setShowUndated}
      />

      {showUndated && data.undated?.length > 0 && (
        <UndatedTray items={data.undated} onSelect={onSelect} />
      )}

      {/* Ribbon */}
      <div className="flex-1 overflow-auto" data-testid="timeline-scroll">
        <div className="relative mx-auto" style={{ width: totalW, minHeight: "100%" }}>
          {/* lane labels (sticky left) */}
          <div className="sticky left-0 z-10 float-left w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#4e6073]" style={{ position: "absolute", left: 8, top: textLaneH - 18, whiteSpace: "nowrap" }}>Texts</div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#4e6073]" style={{ position: "absolute", left: 8, top: textLaneH + 34, whiteSpace: "nowrap" }}>Thinkers</div>
          </div>

          {/* Texts lane (above axis) */}
          <div className="relative" style={{ height: textLaneH }}>
            {texts.map((it) => (
              <TimelineCard key={it.id} item={it} above onSelect={onSelect} activeId={activeId} dimmed={isDimmed(it)} />
            ))}
          </div>

          <EraAxis ticks={ticks} />

          {/* Thinkers lane (below axis) */}
          <div className="relative" style={{ height: personLaneH }}>
            {persons.map((it) => (
              <TimelineCard key={it.id} item={it} above={false} onSelect={onSelect} activeId={activeId} dimmed={isDimmed(it)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
